import React, { useEffect, useState } from "react";
import { Tabs } from "@life-line-lab/components-react";
import { useTranslation } from "react-i18next";
import useQuery from "Hooks/query.hook";
import { Helmet } from "react-helmet";
import ModuleTitleBar from "Components/shared/module-title-bar";
import { isEmpty } from "lodash";
import DownloadOrders from "Components/order-management/plan-schedule/download-orders";
import UploadOrders from "Components/order-management/plan-schedule/upload-orders";
import {
  fetchPlanScheduleEndToEndPayload,
  fetchPlanScheduleEndToEndResult,
  fetchPlanScheduleFirstMidMilePayload,
  fetchPlanScheduleFirstMidMileResult,
  fetchPlanScheduleFirstMilePayload,
  fetchPlanScheduleFirstMileResult,
  fetchPlanScheduleMidLastMilePayload,
  fetchPlanScheduleMidLastMileResult,
  fetchPlanScheduleMidMilePayload,
  fetchPlanScheduleMidMileResult,
  fetchScheduleVersions,
  uploadPlanSchedule,
} from "Services/plan-schedule.services";
import { toaster } from "Helpers/toaster";

// ====================================================
//   Local Helpers
// ====================================================
const downloadFile = (data, name?: string) => {
  // create a readable json
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: "application/json" });

  // create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `schedule${name ? "_" + name : ""}.json`);

  // automatically download by clicking link virtually
  document.body.appendChild(link);
  link.click();

  // cleanup and remove
  link?.parentNode?.removeChild(link);
};

const readFile = async (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const getAllVersions = (data) => {
  let allVersions: any = [];

  for (let i = 0; i < data.length; i++) {
    const versionArray = data[i].versions;

    for (let j = 0; j < versionArray.length; j++) {
      allVersions.push({ label: versionArray[j]?.substring(1), value: versionArray[j] });
    }
  }

  return allVersions;
};

// ====================================================
//   Plan Schedule Functions
// ====================================================
const usePlanSchedule = ({ setIsFileCleared, setVersionList, setIsLoading }) => {
  const { t } = useTranslation();

  const handleFetchVersions = async () => {
    const responseData: any = await fetchScheduleVersions();

    if (responseData && !responseData.error) {
      setVersionList && setVersionList(getAllVersions(responseData.data));
    } else {
      toaster("error", "Unable to fetch versions.");
    }
  };

  const handleFetchFirstMilePayload = async () => {
    const responseData: any = await fetchPlanScheduleFirstMilePayload();

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "payload");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }
  };

  const handleFetchFirstMidMilePayload = async () => {
    const responseData: any = await fetchPlanScheduleFirstMidMilePayload();

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "payload");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }
  };

  const handleFetchMidMilePayload = async () => {
    const responseData: any = await fetchPlanScheduleMidMilePayload();

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "payload");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }
  };

  const handleFetchMidLastMilePayload = async () => {
    const responseData: any = await fetchPlanScheduleMidLastMilePayload();

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "payload");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }
  };

  const handleFetchEndToEndPayload = async () => {
    const responseData: any = await fetchPlanScheduleEndToEndPayload();

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "payload");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }
  };

  const handleUploadPlanSchedule = async (data) => {
    const responseData: any = await uploadPlanSchedule(data);

    if (responseData && !responseData.error) {
      toaster("success", "Successfully uploaded schedule.");
      setIsFileCleared && setIsFileCleared(true);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleFetchFirstMileResult = async (data) => {
    setIsLoading && setIsLoading(true);
    const responseData: any = await fetchPlanScheduleFirstMileResult({ algorithmVersion: data });

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "result");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchFirstMidMileResult = async (data) => {
    setIsLoading && setIsLoading(true);
    const responseData: any = await fetchPlanScheduleFirstMidMileResult({ algorithmVersion: data });

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "result");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchMidMileResult = async (data) => {
    setIsLoading && setIsLoading(true);
    const responseData: any = await fetchPlanScheduleMidMileResult({ algorithmVersion: data });

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "result");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchMidLastMileResult = async (data) => {
    setIsLoading && setIsLoading(true);
    const responseData: any = await fetchPlanScheduleMidLastMileResult({ algorithmVersion: data });

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "result");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }

    setIsLoading && setIsLoading(false);
  };

  const handleFetchEndToEndResult = async (data) => {
    setIsLoading && setIsLoading(true);
    const responseData: any = await fetchPlanScheduleEndToEndResult({ algorithmVersion: data });

    if (responseData && !responseData.error) {
      downloadFile && downloadFile(responseData.data, "result");
    } else {
      toaster("error", t("error.unableDownloadFile", { field: responseData.error.message }));
    }

    setIsLoading && setIsLoading(false);
  };

  return {
    handleFetchVersions,
    handleFetchFirstMilePayload,
    handleFetchFirstMidMilePayload,
    handleFetchMidMilePayload,
    handleFetchMidLastMilePayload,
    handleFetchEndToEndPayload,
    handleUploadPlanSchedule,
    handleFetchFirstMileResult,
    handleFetchFirstMidMileResult,
    handleFetchMidMileResult,
    handleFetchMidLastMileResult,
    handleFetchEndToEndResult,
  };
};

// ====================================================
//   Main Component
// ====================================================
const PlanSchedule = () => {
  const { t } = useTranslation();
  const query = useQuery();

  //   Tabs Switcher
  // ====================================================
  const tabKey = "0"; // querying for later?
  const [tabActiveKey, setTabActiveKey] = useState(tabKey);

  const handleTabClick = (key) => {
    setTabActiveKey(key);
  };

  useEffect(() => {
    if (tabActiveKey === "0") handleFetchVersions();
  }, [tabActiveKey]);

  //   Filters
  // ====================================================
  let initialFilters = {
    versionPayload: "v1.00",
    versionResult: "v1.00",
    deliveryTypePayload: "First-Mile + Mid-Mile",
    deliveryTypeResult: "First-Mile + Mid-Mile",
  };

  const [filterObj, setFilterObj] = useState<any>(initialFilters);

  const onFilter = (filterInput) => {
    if (isEmpty(filterInput)) {
      setFilterObj(initialFilters);
      return;
    }
    let filterValue = {
      ...filterObj,
      versionPayload: filterInput.versionPayload || null,
      deliveryTypePayload: filterInput.deliveryTypePayload || null,
      versionResult: filterInput.versionResult || null,
      deliveryTypeResult: filterInput.deliveryTypeResult || null,
    };

    setFilterObj && setFilterObj(filterValue);
  };

  //   Other states
  // ====================================================
  const [isFileCleared, setIsFileCleared] = useState<boolean>(false);
  const [versionList, setVersionList] = useState<any>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  //   API functions
  // ====================================================
  const {
    handleFetchVersions,
    handleFetchFirstMidMilePayload,
    handleFetchMidMilePayload,
    handleFetchMidLastMilePayload,
    handleFetchEndToEndPayload,
    handleFetchFirstMidMileResult,
    handleFetchMidMileResult,
    handleFetchMidLastMileResult,
    handleFetchEndToEndResult,
    handleUploadPlanSchedule,
  } = usePlanSchedule({ setIsFileCleared, setVersionList, setIsLoading });

  //   Other functions
  // ====================================================
  // for download
  const onGenerate = (type = "payload") => {
    if (type === "result") {
      switch (filterObj.deliveryTypeResult) {
        case "First-Mile + Mid-Mile":
          handleFetchFirstMidMileResult(filterObj.versionResult);
          break;
        case "Mid-Mile Only":
          handleFetchMidMileResult(filterObj.versionResult);
          break;
        case "Mid-Mile + Last-Mile":
          handleFetchMidLastMileResult(filterObj.versionResult);
          break;
        case "End To End":
          handleFetchEndToEndResult(filterObj.versionResult);
          break;
        default:
      }
    } else {
      switch (filterObj.deliveryTypePayload) {
        case "First-Mile + Mid-Mile":
          handleFetchFirstMidMilePayload();
          break;
        case "Mid-Mile Only":
          handleFetchMidMilePayload();
          break;
        case "Mid-Mile + Last-Mile":
          handleFetchMidLastMilePayload();
          break;
        case "End To End":
          handleFetchEndToEndPayload();
          break;
        default:
      }
    }
  };

  // for upload
  const handleConfirm = async (jsonFile) => {
    const unparsedJson: any = await readFile(jsonFile);
    const parsedJson = JSON.parse(unparsedJson);

    handleUploadPlanSchedule(parsedJson);
  };

  //   Tabs Components
  // ====================================================
  const tabs = [
    {
      tabKey: "0",
      label: t("tab.download"),
      component: (
        <DownloadOrders
          onFilter={onFilter}
          onGenerate={onGenerate}
          versionList={versionList}
          isLoading={isLoading}
        />
      ),
    },
    {
      tabKey: "1",
      label: t("tab.upload"),
      component: (
        <UploadOrders
          onFilter={onFilter}
          handleConfirm={handleConfirm}
          isFileCleared={isFileCleared}
        />
      ),
    },
  ];

  return (
    <div className="plan-schedule">
      <Helmet>
        <title>{t("pageTitle.planSchedule")}</title>
      </Helmet>
      <ModuleTitleBar titleText={t("title.planSchedule")} />
      <Tabs
        className="plan-schedule-tab"
        destroyInactiveTabPane={true}
        onTabClick={handleTabClick}
        defaultActiveKey="0"
        activeKey={tabActiveKey}
        tabBarGutter={25}
        tabs={tabs}
      />
    </div>
  );
};

export default PlanSchedule;
